
import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { products, productFeatures, categoryDescriptions } from '../../mocks/products';
import type { ProductCategory } from '../../mocks/products';
import { Link } from 'react-router-dom';

const categories: ProductCategory[] = ['전체', '용접철망', '개비온', '보행매트'];

export default function Products() {
  const [activeCategory, setActiveCategory] = useState<ProductCategory>('전체');

  const filteredProducts = activeCategory === '전체'
    ? products
    : products.filter((p) => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center bg-gradient-to-r from-teal-600 to-teal-700">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">제품소개</h1>
          <p className="text-lg md:text-xl text-white/90">
            용접철망 · 개비온 · 보행매트 전문 제조
          </p>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">제품 카테고리</h2>
            <p className="text-lg text-gray-600">용도에 맞는 최적의 제품을 확인하세요.</p>
          </div>

          {/* Tab Buttons */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-gray-100 rounded-full px-1 py-1">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-6 py-2.5 text-sm font-medium rounded-full transition-all whitespace-nowrap cursor-pointer ${
                    activeCategory === cat
                      ? 'bg-teal-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Category Description */}
          {activeCategory !== '전체' && categoryDescriptions[activeCategory] && (
            <div className="flex items-center justify-center mb-12">
              <div className="bg-teal-50 rounded-lg px-8 py-5 flex items-center max-w-2xl w-full">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-5 flex-shrink-0">
                  <i className={`${categoryDescriptions[activeCategory].icon} text-2xl text-teal-600`}></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{categoryDescriptions[activeCategory].title}</h3>
                  <p className="text-sm text-gray-600">{categoryDescriptions[activeCategory].description}</p>
                </div>
              </div>
            </div>
          )}

          {/* Product Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
            {filteredProducts.map((product) => (
              <div key={product.id} className="bg-gray-50 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all hover:-translate-y-1 duration-300">
                <div className="w-full h-64">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <div className="inline-block px-3 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded-full mb-3">
                    {product.category}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{product.name}</h3>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">{product.description}</p>
                  <div className="bg-teal-50 p-3 rounded-lg">
                    <p className="text-sm font-semibold text-teal-700">{product.specs}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Category Overview Cards */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">제품군 안내</h2>
            <p className="text-lg text-gray-600">3가지 핵심 제품군으로 다양한 현장 요구에 대응합니다.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {Object.entries(categoryDescriptions).map(([key, cat]) => (
              <button
                key={key}
                onClick={() => {
                  setActiveCategory(key as ProductCategory);
                  window.scrollTo({ top: 400, behavior: 'smooth' });
                }}
                className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-all text-left cursor-pointer group"
              >
                <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6 group-hover:bg-teal-600 transition-colors">
                  <i className={`${cat.icon} text-3xl text-teal-600 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{cat.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">{cat.description}</p>
                <span className="text-sm font-medium text-teal-600 flex items-center">
                  제품 보기
                  <i className="ri-arrow-right-line ml-1"></i>
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Product Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">제품 특징</h2>
            <p className="text-lg text-gray-600">우진에코텍 제품의 핵심 장점</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {productFeatures.map((feature, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-lg text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${feature.icon} text-3xl text-teal-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Areas */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">적용 분야</h2>
            <p className="text-lg text-gray-600">다양한 하천정비 현장에 적용 가능합니다.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-water-flash-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">하천 호안 보호</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                하천 제방 및 호안의 침식을 방지하고 구조적 안정성을 확보합니다.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-landscape-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">하상 보호</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                하천 바닥의 세굴을 방지하고 하상을 안정화시킵니다.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-road-map-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">사면 보호</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                급경사 사면의 침식을 방지하고 안정성을 제공합니다.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-flood-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">홍수 방지</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                홍수 시 하천 구조물의 안정성을 유지하고 피해를 최소화합니다.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-leaf-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">생태하천 조성</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                친환경적 하천 복원 및 생태계 서식처를 제공합니다.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-footprint-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">보행로 조성</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                하천 둔치 및 산책로에 안전한 보행 환경을 조성합니다.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            제품에 대해 더 알고 싶으신가요?
          </h2>
          <p className="text-lg text-white/90 mb-8 leading-relaxed">
            우진에코텍의 전문가가 현장 조건에 맞는 최적의 제품을 추천해 드립니다.
          </p>
          <Link 
            to="/contact" 
            className="inline-block px-10 py-4 bg-white text-teal-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            제품 문의하기
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
